import { getCookie } from "cookies-next";
import { axiosInstance } from "../rest/axios";
import {
  DbVisit,
  DbVisitType,
  WalkInCreateRequest,
  WalkInUpdateRequest,
} from "./models";
import { DbBooking } from "../booking/models";
import { calendarService } from "../calendar/service";
import { serviceCategoryService } from "../category-service/service";
import { bookingService } from "../booking/service";
import { DbBusinessCalendar } from "../calendar/models";
import { DbBusinessService } from "../category-service/models";
import {
  collection,
  CollectionReference,
  doc,
  Firestore,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore/lite";
import { DbBusinessPaymentType } from "../business/models";
import { firebaseDb } from "../firebase/client-app";

export class VisitService {
  visitRef(firestore: Firestore): CollectionReference {
    return collection(firestore, "visits");
  }
  async createVisit(payload: WalkInCreateRequest): Promise<DbVisit> {
    const res = await axiosInstance.post("booking/walkin", payload, {
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });
    return res.data as DbVisit;
  }
  async getVisits(
    business_id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit[]> {
    const visitsDocs = await getDocs(
      query(
        this.visitRef(firestore),
        where("business_id", "==", business_id),
        where("type", "==", DbVisitType.WalkIn),
      ),
    );
    return visitsDocs.docs.map((doc) => doc.data() as DbVisit);
  }

  async getVisit(
    business_id: string,
    visit_id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit | null> {
    try {
      const ref = doc(this.visitRef(firestore), visit_id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data() as DbVisit;
      if (data.business_id !== business_id) return null;
      return data;
    } catch (_e) {
      return null;
    }
  }

  async getVisitById(
    visit_id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbVisit | null> {
    try {
      const ref = doc(this.visitRef(firestore), visit_id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data() as DbVisit;
    } catch (_e) {
      return null;
    }
  }

  async getVisitDetailView(
    businessId: string,
    visitId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<{
    visit: DbVisit;
    isWalkIn: boolean;
    services: Array<{
      booking: DbBooking;
      service: DbBusinessService | undefined;
      calendar: DbBusinessCalendar | undefined;
    }>;
    products: DbVisit["products"];
    payments: DbVisit["payments"];
  } | null> {
    const visit = await this.getVisitById(visitId, firestore);
    if (!visit) return null;

    const bookingIds = Array.from(
      new Set(
        (visit.services ?? [])
          .map((s) => s.booking_id)
          .filter(Boolean) as string[],
      ),
    );
    const bookings = bookingIds.length
      ? await bookingService.getBookingsByIds(businessId, bookingIds, firestore)
      : [];

    const serviceKeys = bookings.map((b) => ({
      businessId: b.business_id,
      serviceId: b.service_id,
    }));
    const calendarKeys = bookings.map((b) => ({
      businessId: b.business_id,
      calendarId: b.entity_id,
    }));
    const [services, calendars] = await Promise.all([
      serviceCategoryService.getServicesById(serviceKeys),
      calendarService.getCalendarsById(calendarKeys),
    ]);

    const servicesById = new Map(services.map((s) => [s.id, s]));
    const calendarsById = new Map(calendars.map((c) => [c.id, c]));

    const details = bookings.map((b) => ({
      booking: b,
      service: servicesById.get(b.service_id),
      calendar: calendarsById.get(b.entity_id),
    }));

    return {
      visit,
      isWalkIn: visit.type === DbVisitType.WalkIn,
      services: details,
      products: visit.products,
      payments: visit.payments,
    };
  }

  async addVisitPayment(
    business_id: string,
    visit_id: string,
    type: DbBusinessPaymentType,
    amount: number,
  ): Promise<DbVisit> {
    const response = await axiosInstance.post(
      "/visit/payment",
      {
        business_id: business_id,
        visit_id: visit_id,
        type,
        amount,
      },
      {
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbVisit;
  }

  async undoVisitPayment(
    business_id: string,
    visit_id: string,
  ): Promise<DbVisit> {
    const response = await axiosInstance.delete("/visit/payment", {
      data: {
        business_id: business_id,
        visit_id: visit_id,
      },
      headers: {
        Authorization: getCookie("Authorization")?.toString(),
      },
    });
    return response.data as DbVisit;
  }

  async adjustVisitAmount(visit_id: string, amount: number): Promise<DbVisit> {
    const response = await axiosInstance.put(
      `/visit/${visit_id}`,
      {
        booking_amount: amount,
        visit_id: visit_id,
      },
      {
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbVisit;
  }

  async updateVisit(
    visitId: string,
    payload: WalkInUpdateRequest,
  ): Promise<DbVisit> {
    const res = await axiosInstance.put(`/booking/visit/${visitId}`, payload, {
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });
    return res.data as DbVisit;
  }
}

export const visitService = new VisitService();
